import { useMemo, useState } from 'react';
import type { Card, CampoConfig, Etapa, Flow } from '../../types';

interface CampoAnexoDisponivel {
  id: string;
  nome: string;
  etapaNome: string;
  qtdAnexos: number;
}

/** Campo de anexo que o checkbox de motivos já apontou como reprovado */
interface CampoDerivado {
  campoId: string;
  nome: string;
  motivo: string;
  /** texto da opção marcada no checkbox, para mostrar de onde veio */
  opcao: string;
}

export interface SolicitacaoReenvioPayload {
  campos: { campoId: string; nome: string; motivo: string }[];
  observacao: string;
  etapaRetornoId: string;
}

interface ModalSolicitarReenvioProps {
  card: Card;
  flow: Flow;
  /** Etapa de reprovação para onde o card está indo */
  etapaDestino: Etapa;
  /** Etapa de onde o card saiu — é o retorno padrão quando a etapa não define outro */
  etapaOrigem?: Etapa;
  onConfirmar: (payload: SolicitacaoReenvioPayload) => void;
  onCancelar: () => void;
}

/** Compara nomes ignorando acento, caixa, asterisco e espaço sobrando. */
const normalizar = (texto: string) =>
  (texto || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\*/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/** Todos os campos de anexo do fluxo, com a contagem do que o card tem hoje. */
const listarCamposAnexo = (card: Card, flow: Flow): CampoAnexoDisponivel[] => {
  const vistos = new Set<string>();
  const lista: CampoAnexoDisponivel[] = [];

  for (const etapa of flow.etapas || []) {
    for (const campo of etapa.campos || []) {
      if (campo.tipo !== 'anexo') continue;
      if (vistos.has(String(campo.id))) continue;
      vistos.add(String(campo.id));

      const valor = (card.campos || {})[campo.id];
      lista.push({
        id: String(campo.id),
        nome: campo.nome,
        etapaNome: etapa.nome,
        qtdAnexos: Array.isArray(valor) ? valor.length : valor ? 1 : 0,
      });
    }
  }

  return lista.sort((a, b) => b.qtdAnexos - a.qtdAnexos);
};

/** Lê o checkbox de motivos e casa cada opção marcada com um campo de anexo. */
const anexosDosMotivos = (
  card: Card,
  flow: Flow,
  camposAnexo: CampoAnexoDisponivel[] = listarCamposAnexo(card, flow)
): CampoDerivado[] => {
  const valores = card.campos || {};
  const todosCampos: CampoConfig[] = (flow.etapas || []).flatMap(e => e.campos || []);

  const porNomeExato = new Map<string, CampoAnexoDisponivel>();
  for (const anexo of camposAnexo) porNomeExato.set(normalizar(anexo.nome), anexo);

  const casarComAnexo = (opcao: string): CampoAnexoDisponivel | null => {
    const alvo = normalizar(opcao);
    if (!alvo) return null;
    const exato = porNomeExato.get(alvo);
    if (exato) return exato;
    // O texto da opção às vezes é uma versão encurtada do nome do campo
    // ("Faturamento dos últimos" x "Faturamento dos últimos 12 meses").
    // Só aceito se sobrar um candidato: com dois, pedir o anexo errado é pior
    // do que cair na escolha manual.
    const parciais = camposAnexo.filter(a => {
      const nome = normalizar(a.nome);
      return nome.startsWith(alvo) || alvo.startsWith(nome);
    });
    return parciais.length === 1 ? parciais[0] : null;
  };

  // Pode existir mais de um checkbox no fluxo; fico com o que mais casa com
  // campos de anexo, que é o de "motivos de não aprovação".
  let melhor: CampoDerivado[] = [];

  for (const campo of todosCampos) {
    if (!['checkbox', 'checkbox_multi'].includes(campo.tipo)) continue;
    const selecionadas = valores[campo.id];
    if (!Array.isArray(selecionadas) || selecionadas.length === 0) continue;

    // Justificativa vinculada a este checkbox: { [opção]: motivo }
    const campoJustificativa = todosCampos.find(
      c => c.tipo === 'justificativa_checkbox' && String(c.campoVinculadoId) === String(campo.id)
    );
    const justificativas: Record<string, string> =
      campoJustificativa && valores[campoJustificativa.id] && typeof valores[campoJustificativa.id] === 'object'
        ? valores[campoJustificativa.id]
        : {};

    const encontrados: CampoDerivado[] = [];
    const jaUsados = new Set<string>();
    for (const opcao of selecionadas) {
      const anexo = casarComAnexo(String(opcao));
      if (!anexo || jaUsados.has(anexo.id)) continue;
      jaUsados.add(anexo.id);
      encontrados.push({
        campoId: anexo.id,
        nome: anexo.nome,
        motivo: (justificativas[String(opcao)] || '').trim(),
        opcao: String(opcao),
      });
    }

    if (encontrados.length > melhor.length) melhor = encontrados;
  }

  return melhor;
};

/**
 * Reprovação só por motivo interno: a etapa aponta um campo de motivo interno,
 * ele está preenchido e nenhum motivo de documento foi marcado. Nesse caso não
 * há arquivo para pedir de volta, então o card entra na reprovação sem o modal
 * e sem o ciclo de reenvio (não volta para reanálise).
 */
export const reprovacaoSoPorMotivoInterno = (card: Card, flow: Flow, etapaDestino: Etapa): boolean => {
  const campoId = etapaDestino.motivoInternoCampoId;
  if (!campoId) return false;
  const valor = (card.campos || {})[campoId];
  const preenchido = Array.isArray(valor) ? valor.length > 0 : String(valor ?? '').trim() !== '';
  if (!preenchido) return false;
  return anexosDosMotivos(card, flow).length === 0;
};

/**
 * Modal que abre quando um card cai numa etapa de reprovação configurada para
 * pedir reenvio de anexo.
 *
 * Quando o fluxo já tem um checkbox de motivos apontando os anexos reprovados
 * (com a justificativa de cada um), o modal lê dali e só pede confirmação —
 * digitar o mesmo motivo duas vezes é trabalho repetido e abre espaço para as
 * duas versões divergirem. Sem esse checkbox, cai na escolha manual.
 */
const ModalSolicitarReenvio = ({
  card,
  flow,
  etapaDestino,
  etapaOrigem,
  onConfirmar,
  onCancelar,
}: ModalSolicitarReenvioProps) => {
  const camposAnexo = useMemo(() => listarCamposAnexo(card, flow), [flow, card]);
  const derivados = useMemo(() => anexosDosMotivos(card, flow, camposAnexo), [card, flow, camposAnexo]);

  const [modoManual, setModoManual] = useState(derivados.length === 0);
  const [selecionados, setSelecionados] = useState<Record<string, boolean>>(
    () => Object.fromEntries(derivados.map(d => [d.campoId, true]))
  );
  const [motivos, setMotivos] = useState<Record<string, string>>(
    () => Object.fromEntries(derivados.map(d => [d.campoId, d.motivo]))
  );
  const [observacao, setObservacao] = useState('');
  const [etapaRetornoId, setEtapaRetornoId] = useState<string>(
    etapaDestino.etapaRetornoReenvioId || String(etapaOrigem?.id || card.etapaId)
  );

  const marcados = camposAnexo.filter(c => selecionados[c.id]);
  const etapaRetorno = (flow.etapas || []).find(e => String(e.id) === String(etapaRetornoId));
  const total = modoManual ? marcados.length : derivados.length;

  const toggle = (campoId: string) => {
    setSelecionados(prev => ({ ...prev, [campoId]: !prev[campoId] }));
  };

  const confirmar = () => {
    const campos = modoManual
      ? marcados.map(c => ({ campoId: c.id, nome: c.nome, motivo: (motivos[c.id] || '').trim() }))
      : derivados.map(d => ({ campoId: d.campoId, nome: d.nome, motivo: d.motivo }));
    if (campos.length === 0) return;
    onConfirmar({ campos, observacao: observacao.trim(), etapaRetornoId: String(etapaRetornoId) });
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Cabeçalho */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                📎 {modoManual ? 'Qual anexo precisa ser reenviado?' : 'Confirmar pedido de reenvio'}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                O card vai para <strong>{etapaDestino.nome}</strong>. O solicitante verá só os campos
                listados aqui no acompanhamento do portal.
              </p>
            </div>
            <button
              type="button"
              onClick={onCancelar}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              title="Cancelar"
            >
              ✕
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2 truncate">Card: {card.titulo}</p>
        </div>

        {/* Corpo */}
        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          {!modoManual ? (
            <>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
                <p className="text-xs text-emerald-800 font-medium mb-2">
                  Peguei dos motivos de não aprovação que você já preencheu:
                </p>
                <div className="space-y-2">
                  {derivados.map(d => (
                    <div key={d.campoId} className="bg-white rounded-lg border border-emerald-100 p-2.5">
                      <div className="font-medium text-sm text-gray-900">{d.nome}</div>
                      {d.motivo ? (
                        <div className="text-xs text-gray-600 mt-0.5">{d.motivo}</div>
                      ) : (
                        <div className="text-xs text-gray-400 mt-0.5 italic">
                          sem justificativa preenchida — o solicitante verá só o nome do campo
                        </div>
                      )}
                      {normalizar(d.opcao) !== normalizar(d.nome) && (
                        <div className="text-[10px] text-gray-400 mt-1">marcado como “{d.opcao}”</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setModoManual(true)}
                className="text-xs text-indigo-600 hover:text-indigo-800 underline"
              >
                Escolher outros campos manualmente
              </button>
            </>
          ) : camposAnexo.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              Este fluxo não tem nenhum campo de anexo para devolver.
            </p>
          ) : (
            <>
              {derivados.length > 0 && (
                <button
                  type="button"
                  onClick={() => setModoManual(false)}
                  className="text-xs text-indigo-600 hover:text-indigo-800 underline"
                >
                  ← Voltar para o que veio dos motivos de não aprovação
                </button>
              )}
              <div className="space-y-2">
                {camposAnexo.map(campo => {
                  const marcado = !!selecionados[campo.id];
                  return (
                    <div
                      key={campo.id}
                      className={`border rounded-xl p-3 transition-colors ${
                        marcado ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white'
                      }`}
                    >
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={marcado}
                          onChange={() => toggle(campo.id)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900">{campo.nome}</div>
                          <div className="text-[11px] text-gray-500">
                            {campo.etapaNome} ·{' '}
                            {campo.qtdAnexos > 0
                              ? `${campo.qtdAnexos} arquivo(s) enviado(s)`
                              : 'sem arquivo'}
                          </div>
                        </div>
                      </label>

                      {marcado && (
                        <input
                          type="text"
                          value={motivos[campo.id] || ''}
                          onChange={e => setMotivos(prev => ({ ...prev, [campo.id]: e.target.value }))}
                          placeholder="O que está errado? (ex.: nota ilegível, documento vencido)"
                          className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400/40 focus:border-red-400"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Observação geral (opcional)
            </label>
            <textarea
              value={observacao}
              onChange={e => setObservacao(e.target.value)}
              rows={2}
              placeholder="Mensagem que o solicitante vai ler junto com o pedido"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-gray-400/30"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Quando ele reenviar, mover o card para
            </label>
            <select
              value={etapaRetornoId}
              onChange={e => setEtapaRetornoId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            >
              {(flow.etapas || [])
                .filter(e => String(e.id) !== String(etapaDestino.id))
                .map(e => (
                  <option key={e.id} value={String(e.id)}>
                    {e.nome}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {/* Rodapé */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            {total === 0
              ? 'Marque ao menos um campo'
              : `${total} campo(s) · volta para ${etapaRetorno?.nome || 'etapa anterior'}`}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancelar}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={confirmar}
              disabled={total === 0}
              className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reprovar e pedir reenvio
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalSolicitarReenvio;
